// ManageAnnouncementsNew.tsx - Functional Component
import NewAnnouncementForm from "components/NewAnnouncementsForm";
import { RouteProps } from "containers/App";
import Constants from "helpers/constants";
import { Announcement as AnnouncementProps } from "interfaces/announcement";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps, useSelector } from "react-redux";
import { State } from "state";
import { createAnnouncement, deleteAnnouncement } from "state/announcement";
import { fetchMyAnnouncements } from "state/announcements";
import { trackPageView, PAGEVIEW_EVENTS } from "state/userEvents";
import BackLink from "components/BackLink";
import { RouteComponentProps, withRouter } from "react-router-dom";
import FilePreviewModal from "components/FilePreviewModal";
import Outline from "../assets/img/-outline.svg";
import eye from "../assets/img/eye.svg";
import { Funnel } from "lucide-react";
import StoresPopup from "components/StoresPopup";
import Loading from "../components/Loading";
import { CircularProgress } from "@material-ui/core";
import Loading2 from "components/Loading2";
// ==================== Announcement List Components ====================

interface AnnouncementItemProps {
  announcement: AnnouncementProps;
}

interface AnnLoadingState {
  loading?: boolean;
}

const AnnouncementItem: React.FC<AnnouncementItemProps> = ({ announcement }) => {

  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    url: string;
    name: string;
  }>({
    isOpen: false,
    url: "",
    name: "",
  });
  const [showStoresPopup, setShowStoresPopup] = useState(false);
  const funnelIconRef = useRef<HTMLDivElement>(null);

  // Parse the body if it's stringified
  const parsedBody = (() => {
    try {
      return typeof announcement.body === "string" && announcement.body.startsWith('"')
        ? JSON.parse(announcement.body)
        : announcement.body;
    } catch {
      return announcement.body;
    }
  })();

  // Strip HTML tags for preview
  const stripHtml = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const contentPreview = stripHtml(parsedBody).substring(0, 300);

  const isPreviewable = (fileName: string): boolean => {
    // Extract filename if it's a URL
    const actualFileName = fileName.includes('/') ? fileName.split('/').pop() || fileName : fileName;

    // Remove query parameters if present
    const cleanFileName = actualFileName.split('?')[0];

    // Get extension from the clean filename
    const extension = cleanFileName.split(".").pop()?.toLowerCase() || "";
    const previewableTypes = ["jpg", "jpeg", "png", "gif", "webp", "pdf"];

    return previewableTypes.includes(extension);
  };

  const handleAttachmentClick = (url: string, name?: string, index?: number) => {
    const fileName = name || url.split("/").pop() || "File";
    const canPreview = isPreviewable(fileName);
    
    console.log("File clicked:", { fileName, url, canPreview });

    // Build API URL with file index for correct attachment
    const apiUrl = announcement.id && typeof index === 'number'
      ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcement.id}/${index}`
      : url;
    if (canPreview) {
      // Open preview modal for images and PDFs
      setPreviewModal({
        isOpen: true,
        url: apiUrl,
        name: fileName,
      });
    } else {
      // Direct download for other file types
      handleDownload(apiUrl, fileName);
    }
  };

  const handleDownload = (apiUrl: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = apiUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: "",
      name: "",
    });
  };

  return (
    <div style={styles.announcementItem}>
      <div style={styles.announcementHeader}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <p style={styles.announcementTitle}>
            {announcement.title.slice(0, 1).toLocaleUpperCase() + announcement.title.slice(1)}
          </p>
          <div
            ref={funnelIconRef}
            style={{ cursor: "pointer", position: "relative" }}
            onClick={() => setShowStoresPopup(!showStoresPopup)}
          >
            {(announcement?.stores?.length ?? 0) !== 0 && <Funnel height={16} />}
            {(announcement?.stores?.length ?? 0) !== 0 && <StoresPopup
              stores={announcement.stores || []}
              isOpen={showStoresPopup}
              onClose={() => setShowStoresPopup(false)}
              anchorRef={funnelIconRef}
            />}
          </div>
        </div>
        <p style={styles.chainName}>
          {announcement.chain.name} - {announcement.project.name}
        </p>

        <p style={styles.dateStyle}>
          {moment(announcement.dateCreated).format("M/DD/YYYY")} -{" "}
          {`By ${announcement.user.firstName} ${announcement.user.lastName}`}
        </p>
      </div>
      <div style={styles.announcementBody}>
        {contentPreview}
        {parsedBody.length > 300 ? "..." : ""}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-start" }}>
        {announcement.allAttachments?.map((item, index) => {
          const canPreview = isPreviewable(item.name);
          const apiUrl = announcement.id !== undefined ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcement.id}/${index}` : (item.name);

          return (
            <div
              style={styles.attachmentButton}
              key={index}
              onClick={() => handleAttachmentClick(apiUrl, item.name, index)}
              title={`${item.name} - ${canPreview ? "Click to preview" : "Click to download"}`}
            >
              {canPreview ? (
                <img
                  src={eye}
                  alt="preview"
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                />
              ) : (
                <img
                  src={Outline}
                  alt="download"
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                />
              )}
            </div>
          );
        })}
      </div>

      <FilePreviewModal
        isOpen={previewModal.isOpen}
        fileUrl={previewModal.url}
        fileName={previewModal.name}
        onClose={handleClosePreview}
      />
    </div>
  );
};

interface AnnouncementListProps {
  data: AnnouncementProps[];
  loading?: boolean;
}

const AnnouncementList: React.FC<AnnouncementListProps> = ({ data, loading }) => {
  if (loading) {
    // return <CircularProgress size={32} style={{ color: "#FDF19EC2" }} />;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // height: "100%",
          width: "100%",
        }}
      >
        <Loading2 color={"#ffe854d3"} height={80} width={80} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={{ color: "#999", fontSize: 14 }}>No announcements yet</p>
      </div>
    );
  }

  return (
    <div style={styles.listContainer}>
      {/* <h2 style={styles.listTitle}>Recent Announcements</h2> */}
      {data.map((announcement) => (
        <AnnouncementItem key={announcement.id} announcement={announcement} />
      ))}
    </div>
  );
};

// ==================== Main Component ====================

type ManageAnnouncementsProps = PropsFromRedux & RouteProps & RouteComponentProps & AnnLoadingState;

const ManageAnnouncementsNew: React.FC<ManageAnnouncementsProps> = (props) => {
  const { user, announcements = [], isMobile, history, loading } = props;
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  const filteredAnnouncements = selectedProjectId
    ? announcements.filter(
        (a) => a.project?.id === selectedProjectId
      )
    : [];

  useEffect(() => {
    props.fetchMyAnnouncements().then(() => {
      props.trackPageView({
        event_type: PAGEVIEW_EVENTS.ANNOUNCEMENTS_PAGEVIEW,
        user_id: user?.id,
        store_id: null,
        project_id: null,
        timestamp: Date.now(),
      });
    });
  }, []);

  return (
    <div style={styles.pageWrapper(isMobile)}>
      <div style={styles.contentContainer(isMobile)}>
        {/* Left side - Form */}
        <div style={styles.formArea}>
          <NewAnnouncementForm {...props} onProjectChange={(projectId: number | null) => setSelectedProjectId(projectId)} />
        </div>

        {/* Right side - Announcements List */}
        <div style={styles.listArea}>
          {!selectedProjectId ? (
          <div style={{ padding: 20, color: "#999", fontSize: 14, textAlign: "center" }}>
            Please select a project to view announcements
          </div>
          ) : (
          <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <span
              style={{
                color: "#E5B42F",
                borderBottom: "1px solid #E5B42F",
                lineHeight: "15px",
                margin: "0 0 10px 0",
                cursor: "pointer",
              }}
              onClick={() => history.push( selectedProjectId
                    ? `/announcements/my-announcements?projectId=${selectedProjectId}`
                    : `/announcements/my-announcements`
                )
              }
            >
              View All
            </span>
          </div>
          <AnnouncementList data={filteredAnnouncements} loading={loading} /> 
          </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Redux Connection ====================

const connector = connect(
  (state: State) => ({
    user: state.auth_user.content,
    announcements: state.announcements.content,
    loading: state.announcements.loading,
    hasErrors: state.surveys.hasError,
    errors: state.surveys.errors,
    isMobile: state.breakpoint.content === Constants.BREAKPOINT_MOBILE,
    currentUserId: state.auth_user.content?.id,
    isAdmin: state.auth_user.content?.role === Constants.AB_SYSTEM_ADMIN,
  }),
  {
    fetchMyAnnouncements,
    createAnnouncement,
    deleteAnnouncement,
    trackPageView,
  }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

// export default connector(ManageAnnouncementsNew);

export default withRouter(connector(ManageAnnouncementsNew));

// ==================== Styles ====================

const styles = {
  pageWrapper: (isMobile: boolean): React.CSSProperties => ({
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    // backgroundColor: "#f5f5f5",
    // padding: 20,
    boxSizing: "border-box",
    margin: isMobile ? 0 : "0 auto",
  }),
  contentContainer: (isMobile: boolean) => ({
    display: "flex",
    flexDirection: isMobile ? ("column" as const) : ("row" as const),
    gap: 20,
    width: "100%",
  }),
  formArea: {
    flex: 2,
  } as React.CSSProperties,
  listArea: {
    flex: 1,
  } as React.CSSProperties,
  listContainer: {
    padding: 0,
    height: "100%",
  } as React.CSSProperties,
  listTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#333",
    marginBottom: 20,
    marginTop: 0,
  } as React.CSSProperties,
  announcementItem: {
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 16,
    marginBottom: 15,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
  } as React.CSSProperties,
  announcementHeader: {
    color: "#666",
    fontSize: 12,
    // marginBottom: 8,
  } as React.CSSProperties,
  announcementTitle: {
    fontSize: 14,
    fontWeight: 400,
    // marginBottom: 10,
    color: "#333",
    padding: 0,
    margin: 0,
  } as React.CSSProperties,
  chainName: {
    fontWeight: 500,
    // fontStyle: Medium,
    fontSize: 16,
    color: "#000",
    padding: 0,
    margin: 0,
    lineHeight: 1.4,
  } as React.CSSProperties,
  dateStyle: {
    fontWeight: 400,
    fontSize: "12px",
    color: "#929497",
    padding: 0,
    margin: "5px 0 5px 0",
  } as React.CSSProperties,
  announcementBody: {
    fontSize: 12,
    lineHeight: 1.6,
    color: "#000",
    // marginBottom: 10,
    fontWeight: 400,
  } as React.CSSProperties,

  emptyState: {
    backgroundColor: "#fff",
    padding: 40,
    borderRadius: 16,
    textAlign: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
  } as React.CSSProperties,

  attachmentButton: {
    height: "20px",
    width: "20px",
    cursor: "pointer",
    transition: "transform 0.2s",
  } as React.CSSProperties,

  attachmentButtonHover: {
    transform: "scale(1.1)",
  } as React.CSSProperties,
};
